import React, { FC, useEffect, useCallback, useState } from "react";
import { Box, Button, ThemeProvider, Snackbar, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  DataGridPro,
  GridFooterContainer,
  GridPagination,
  useGridApiRef,
  GridSortModel,
  esES,
  enUS,
  frFR,
  deDE,
} from "@mui/x-data-grid-pro";
import themeInarix from "../../StylesMuiInarix";
import { useDispatch, useSelector } from "react-redux";
import {
  initialState,
  isViewerOnly,
  localeData,
  setColumns,
  setColumnOrder,
  setIsOpenFilters,
  setSideBarFiltersVisible,
  setFilterModel,
  setShowFilterForm,
} from "../../../redux/slices/dataTable/data";
import {
  setAppliedView,
  setAppliedViewName,
} from "../../../redux/slices/dataTable/dataViews";

import DataTableColumns from "./columnsAndActions/ColumnsAndActions";
import MenuOptions from "./MenuOptions/MenuOptions";
import { SwitchesStyles } from "./MenuOptions/options/columnSwitches/ColumnSwitchesStyles";
import { TableStyles } from "./TableStyles";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { dataTableActionNewFilter } from "../../../redux/actions/dataTable/DataTableAsyncActions";
import {
  selectDataTableUnifiedState,
  selectExportDataState,
} from "../../../redux/selectors/unifiedSelectors";
import { setTableDataOptions } from "../../../redux/slices/UnifiedSlices/SharedDataReducer";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setColumn: (columns: any) => dispatch(setColumns(columns)),
    setColumnOrder: (columnOrder: string[]) =>
      dispatch(setColumnOrder(columnOrder)),
    setSideBarFiltersVisible: (visible: boolean) => {
      dispatch(setSideBarFiltersVisible(visible));
      dispatch(setIsOpenFilters(initialState.isOpenFilters));
    },
    setNewPage: (
      tableOptions: {
        page: number;
        rowOptions: number;
        totalRows: number | null;
      },
      page: number,
      rowOptions: number
    ) => {
      dispatch(setTableDataOptions({ ...tableOptions, page, rowOptions }));
      dispatch(dataTableActionNewFilter());
    },

    setFilterModel: (filterModel: any) => {
      dispatch(setFilterModel(filterModel));
    },

    setShowFilterForm: (show: boolean) => {
      dispatch(setShowFilterForm(show));
    },

    setAppliedView: (applied: boolean) => {
      dispatch(setAppliedView(applied));
    },

    setAppliedViewName: (name: string | null) => {
      dispatch(setAppliedViewName(name));
    },
  };
};

const DataTable: FC = () => {
  const [t] = useTranslation("global");
  const {
    setColumn,
    setColumnOrder,
    setNewPage,
    setSideBarFiltersVisible,
    setFilterModel,
    setAppliedView: resetAppliedView,
    setAppliedViewName: resetAppliedViewName,
  } = SetActions();

  // Add sorting state
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "createdAt", sort: "desc" },
  ]);

  // Add success message state for applied view
  const [showAppliedViewMessage, setShowAppliedViewMessage] = useState(false);

  const apiRef = useGridApiRef();
  const locale = useSelector(localeData);
  const viewerOnly = useSelector(isViewerOnly);

  const { columnsDataTable, actionsDataTable } = DataTableColumns();

  const {
    tableOptions,
    columnsVisibility,
    loading,
    filtersSideBarVisible,
    pinnedColumns,
    columnOrder,
    filters,
    dataViews,
    columns,
  } = useSelector(selectDataTableUnifiedState);

  const loadingFilters = filters.loadingFilters;
  const filtersSelected = filters.filtersSelectedLength;
  const appliedView = dataViews.appliedView;
  const appliedViewName = dataViews.appliedViewName;

  const { loadingExport } = useSelector(selectExportDataState);

  const applyColumnOrder = useCallback(() => {
    if (
      columnOrder &&
      columnOrder.length > 0 &&
      apiRef.current &&
      columnsDataTable.length > 0
    ) {
      // get the current order of columns in the table
      const currentColumns = apiRef.current.getAllColumns();
      const currentOrder = currentColumns.map((col) => col.field);

      // Compare the columns actual orders
      const isOrderDifferent =
        JSON.stringify(currentOrder) !== JSON.stringify(columnOrder);

      if (isOrderDifferent) {
        // Just order if is necessary, but exclude pinned columns from reordering
        const currentFieldsSet = new Set(currentOrder);
        const pinnedFields = new Set([
          ...(pinnedColumns.left || []),
          ...(pinnedColumns.right || []),
        ]);

        const validOrder = columnOrder.filter(
          (field) => currentFieldsSet.has(field) && !pinnedFields.has(field)
        );

        if (validOrder.length > 0) {
          // Reorder only non-pinned columns
          let targetIndex = 0;

          // Skip pinned left columns when determining target index
          if (pinnedColumns.left && pinnedColumns.left.length > 0) {
            targetIndex = pinnedColumns.left.length;
          }

          for (let i = 0; i < validOrder.length; i++) {
            const field = validOrder[i];
            const currentIndex = apiRef.current.getColumnIndex(field);
            const newIndex = targetIndex + i;

            if (currentIndex !== newIndex) {
              apiRef.current.setColumnIndex(field, newIndex);
            }
          }
        }
      }
    }
  }, [columnOrder, columnsDataTable, apiRef, pinnedColumns]);

  useEffect(() => {
    applyColumnOrder();
  }, [applyColumnOrder]);

  // Handle applied view success message
  const loadings = loading || loadingExport || loadingFilters;

  const paginationModel = {
    pageSize: tableOptions.rowOptions,
    page: tableOptions.page,
  };

  // Handle applied view success message
  useEffect(() => {
    if (appliedView) {
      setShowAppliedViewMessage(true);

      setTimeout(() => {
        resetAppliedView(false);
      }, 100);
    }
  }, [appliedView, resetAppliedView]);

  const handlePageChange = (page: number, rowOption: number) => {
    setNewPage(tableOptions, page, rowOption);
  };

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const handleCloseAppliedViewMessage = () => {
    setShowAppliedViewMessage(false);

    resetAppliedViewName(null);
  };

  const getDataGridLocale = () => {
    switch (locale) {
      case "es":
        return esES.components.MuiDataGrid.defaultProps.localeText;
      case "fr":
        return frFR.components.MuiDataGrid.defaultProps.localeText;
      case "de":
        return deDE.components.MuiDataGrid.defaultProps.localeText;
      case "en":
      default:
        return enUS.components.MuiDataGrid.defaultProps.localeText;
    }
  };

  const handleColumnWidthChange = useCallback(() => {
    if (apiRef.current && columns) {
      const gridColumns = apiRef.current.getAllColumns();

      const updatedReduxColumns = columns.map((originalCol) => {
        const gridCol = gridColumns.find(
          (gc) => gc.field === originalCol.field
        );
        if (gridCol && gridCol.width !== originalCol.width) {
          return { ...originalCol, width: gridCol.width };
        }
        return originalCol;
      });

      setColumn(updatedReduxColumns);
    }
  }, [apiRef, columns, setColumn]);

  const handleColumnOrderChange = useCallback(() => {
    setTimeout(() => {
      if (apiRef.current && columns) {
        const gridColumns = apiRef.current.getAllColumns();
        const columnOrderArray = gridColumns.map((col) => col.field);

        const updatedReduxColumns = columns.map((originalCol) => {
          const gridCol = gridColumns.find(
            (gc) => gc.field === originalCol.field
          );
          if (gridCol && gridCol.width !== originalCol.width) {
            return { ...originalCol, width: gridCol.width };
          }
          return originalCol;
        });

        setColumn(updatedReduxColumns);
        setColumnOrder(columnOrderArray);
      }
    }, 200);
  }, [apiRef, columns, setColumn, setColumnOrder]);

  const paginationToolbar = () => {
    return (
      <GridFooterContainer sx={{ alignItems: "baseline" }}>
        <div className="grid-foot-container">
          {tableOptions.page > 0 ? (
            <Button
              variant="contained"
              onClick={() => handlePageChange(0, tableOptions.rowOptions)}
              disabled={tableOptions.page < 1}
            >
              {t("Data.table.Restart_page")}
            </Button>
          ) : (
            ""
          )}
          <GridPagination />
        </div>
      </GridFooterContainer>
    );
  };

  return (
    <Box
      onClick={
        filtersSideBarVisible ? () => setSideBarFiltersVisible(false) : () => {}
      }
      sx={SwitchesStyles.DataTableBox}
    >
      <MenuOptions loadings={loadings} isViewerOnly={viewerOnly} />
      <ThemeProvider theme={themeInarix(locale)}>
        <DataGridPro
          loading={loading || columnsDataTable.length === 0}
          className="table-styles-data"
          columns={columnsDataTable ?? []}
          pinnedColumns={pinnedColumns}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnWidthChange={handleColumnWidthChange}
          rows={actionsDataTable}
          // Add sorting props
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMode="server"
          filterMode="server"
          onFilterModelChange={(newFilterModel) => {
            setFilterModel(newFilterModel);
          }}
          columnVisibilityModel={columnsVisibility}
          paginationModel={paginationModel}
          slots={{ footer: paginationToolbar }}
          onPaginationModelChange={(newPage) => {
            handlePageChange(newPage.page, newPage.pageSize);
          }}
          rowCount={tableOptions.totalRows ?? 0}
          initialState={{
            pagination: {
              paginationModel: { pageSize: tableOptions.rowOptions },
            },
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          localeText={{
            ...getDataGridLocale(),

            noRowsLabel:
              filtersSelected === 0
                ? t("Data.no_results_data")
                : t("Data.no_results_filters"),
          }}
          pagination
          rowHeight={45}
          pageSizeOptions={[100, 125, 150]}
          apiRef={apiRef}
        />
      </ThemeProvider>

      {/* Success message for applied view */}
      <Snackbar
        open={showAppliedViewMessage}
        autoHideDuration={4000}
        onClose={(event, reason) => {
          handleCloseAppliedViewMessage();
        }}
        anchorOrigin={TableStyles.snackbarPosition}
        sx={TableStyles.successSnackbar}
      >
        <Alert
          onClose={handleCloseAppliedViewMessage}
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon />}
          sx={TableStyles.successAlert}
        >
          {appliedViewName
            ? t("Data_views.view_applied_successfully", {
                viewName: appliedViewName,
              })
            : t("Data_views.view_applied_successfully", { viewName: "" })}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataTable;
